import React, { type ComponentPropsWithRef, type ComponentType } from "react";
import { CallState, type TripProduct } from "@b2bportal/core-types";
import type { BaseProductSet } from "../../../../types";
import type { TripsListProps } from "./types";

export const useTripsList = <Products extends BaseProductSet>(
  props: TripsListProps<Products>
) => {
  const { products } = props;

  const productsList = React.useMemo(() => {
    return Object.values(products);
  }, [products]);

  React.useEffect(() => {
    for (const product of productsList) {
      product.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isLoading, hasError } = React.useMemo(
    () =>
      productsList.reduce(
        (prev, product) => {
          if ("status" in product.reservations) {
            return {
              isLoading: prev.isLoading || product.reservations.isLoading,
              hasError: prev.hasError || product.reservations.isError,
            };
          } else {
            return {
              isLoading:
                prev.isLoading ||
                product.reservations.state === CallState.InProcess,
              hasError:
                prev.hasError ||
                product.reservations.state === CallState.Failed,
            };
          }
        },
        {
          isLoading: false,
          hasError: false,
        }
      ),
    [productsList]
  );

  const trips = React.useMemo(
    () =>
      // The generic here isn't strictly necessary, but it's nice to have
      // because it enforces type consistency within `TripProduct`. Without it,
      // TypeScript would infer `TripProduct<unknown>` for the `product`
      // parameter, which affects the `render` input, allowing it to take
      // anything as an argument, which means that calls to `render(...)`
      // wouldn't be correctly validated.
      productsList
        .flatMap(function <Reservation>(product: TripProduct<Reservation>) {
          return (
            product.reservations.data?.map((wrapper) => ({
              ...wrapper,
              // eslint-disable-next-line @typescript-eslint/no-empty-object-type
              component: (props: ComponentPropsWithRef<ComponentType<{}>>) => (
                <product.component
                  reservation={wrapper.reservation}
                  {...props}
                />
              ),
            })) ?? []
          );
        })
        // Sort by date in descending order, with most recent trips first.
        .sort((a, b) => {
          const { recentFirst } = a;
          const dateA = new Date(a.sortDate);
          const dateB = new Date(b.sortDate);
          return recentFirst
            ? dateB.getTime() - dateA.getTime()
            : dateA.getTime() - dateB.getTime();
        }),
    [productsList]
  );

  return {
    context: { trips, hasError, isLoading },
  };
};
